import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Typography, Divider, Paper, Grid, Box, Button, TextField, MenuItem, FormControl, InputLabel, Select, Avatar, Stack, Checkbox, Autocomplete } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm, Controller } from "react-hook-form";
import { useFileUpload } from 'core/hooks/useFileUpload';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { sendNotifications } from 'services/notifications';
import { getAdminList } from 'services/admin';
import { getVendorList } from 'services/vendors';
import { getStudentList } from 'services/students';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const NotificationsPage = () => {

  const defaultFormFields = useMemo(() => ({
    body: '',
    tag: '',
    title : '',
    userType: '',
    notificationTypes: [ ],
    userIds: [ ],
  }),[]);
  const fileUploadInutRef = useRef(null);

  const [notificationImage, setNotificationImage] = useState();
  const [fileInput, setFileInput] = useState();
  const [adminUsers, setAdminUsers] = useState([]);

  const { handleSubmit, control, formState: { errors }, reset, watch, setValue } = useForm({
    mode: 'onTouched',
    defaultValues: {...defaultFormFields}
  });
  const { enqueueSnackbar } = useSnackbar();

  const [uploadPhoto] = useFileUpload({
		onStarting: (file) => {
      setFileInput(file);
			var reader = new FileReader();
			reader.readAsDataURL(file)
			reader.onloadend = () => setNotificationImage(reader.result);
		}
  });

  const resetForm = function() {
    reset({...defaultFormFields});
    clearImage();
    setValue('userType', '');
  }

  function clearImage(){
    setFileInput(null);
    setNotificationImage(null);
  }

  const onSubmit = (data) => {
    saveData(data);
  };

  const saveData = (data) => {
    const postData = {
      ...data,
      userIds: data.userIds.map(user=>user.userId),
      image: fileInput
    };
    if(!postData.image){
      delete postData.image;
    }
    sendNotifications(postData).then(({response, error}) => {
      if(error){
        enqueueSnackbar('Failed to sent notification', {variant: 'error'});
        setFileInput(null);
      }else{
        enqueueSnackbar('Notifications sent', {variant: 'success'});
        resetForm(null);
      }
    });
  };

  // const isAllOptionSelected = (selectedOptions) => {
  //   let isOptionSelected = false
  //   for (let i = 0; i < selectedOptions.length; i++) {
  //     const option = selectedOptions[i];

  //     if (option.userId === -1) { 
  //       isOptionSelected = true;
  //       break; 
  //     }
  //   }
  //   return isOptionSelected;
  // };

  const onNotificationPhotoClick = useCallback(() => {
    if(fileUploadInutRef?.current?.click){
      fileUploadInutRef.current.click();
    }
  },[fileUploadInutRef]);

  const userType = watch('userType');

  const getAdminUserList = useCallback(() => {
    getAdminList().then(({response, error}) => {
      if(error){
        enqueueSnackbar('No admin users found', {variant: 'error'});
      }else{
        setAdminUsers([{displayName: 'All Users', userId: -1}, ...response]);
      }
    });
  },[enqueueSnackbar]);

  const getVendors = useCallback(() => {
    getVendorList().then(({response, error}) => {
      if(error){
        enqueueSnackbar('No admin users found', {variant: 'error'});
      }else{
        setAdminUsers([{displayName: 'All Users', userId: -1}, ...response]);
      }
    });
  },[enqueueSnackbar]);

  const getStudents = useCallback(() => {
    getStudentList().then(({response, error}) => {
      if(error){
        enqueueSnackbar('No admin users found', {variant: 'error'});
      }else{
        setAdminUsers([{displayName: 'All Users', userId: -1}, ...response]);
      }
    });
  },[enqueueSnackbar]);
  
  useEffect(() => {
    if(userType){
      setAdminUsers([]);
      switch(userType) {
        case 'ADMIN':
          getAdminUserList();
          break;
        case 'VENDOR':
          getVendors();
          break;
        case 'STUDENT':
          getStudents()
          break;
        default:
      }

    }
  }, [userType, getAdminUserList, getVendors, getStudents]);

  const filterOptions = useCallback((options, { inputValue }) => {
    if(inputValue){
      return options.filter((option) => option?.displayName?.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase()) || option?.mobile?.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase()));
    }
    return options;
  }, []);
  

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
      <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
        Notifications
      </Typography>
      <Divider />
      <Box noValidate component="form"  onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }} >
        <Grid container>
          <Grid container item xs={12} sm={12} md={6} xl={4} lg={4} >
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Controller
                control={control}
                name="title"
                id="title"
                rules={{ required: "Enter notification title"}}
                render={({field}) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Notification Title"
                    required
                    error={!!errors?.title}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Controller
                control={control}
                name="body"
                id="body"
                rules={{ required: "Enter notification body"}}
                render={({field}) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Notification Body"
                    required
                    error={!!errors?.body}
                    size="small"
                    fullWidth
                    multiline
                    minRows={5}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} sx={{mt: 2}}>
              <Controller
                control={control}
                name="notificationTypes"
                id="notificationTypes"
                rules={{ required: "Select notification types"}}
                render={({field}) => (
                  <FormControl fullWidth  {...field} error={!!errors?.notificationTypes}>
                    <InputLabel required id="type-select-label" size="small">Notification Types</InputLabel>
                    <Select
                      required
                      size="small"
                      labelId="type-select-label"
                      id="type-select"
                      label="Notification Types"
                      value={field?.value}
                      onChange={field.onChange}
                      fullWidth
                      multiple
                    >
                      <MenuItem value="EMAIL">Email</MenuItem>
                      <MenuItem value="PUSH">Push</MenuItem>
                      <MenuItem value="SMS">SMS</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Controller
                control={control}
                name="tag"
                id="tag"
                rules={{ required: "Enter notification body"}}
                render={({field}) => (
                  <TextField
                    {...field}
                    required
                    margin="normal"
                    label="Tag"
                    error={!!errors?.tag}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12}  sx={{mt: 1.8}}>
              <Controller
                control={control}
                name="userType"
                id="userType"
                rules={{ required: "Select user type"}}
                render={({field}) => (
                  <FormControl fullWidth  {...field} error={!!errors?.userType}>
                    <InputLabel required id="user-type-select-label" size="small">User Type</InputLabel>
                    <Select
                      required
                      size="small"
                      labelId="user-type-select-label"
                      id="user-type-select"
                      label="User Type"
                      defaultValue=''
                      value={(field.value) || ''}
                      onChange={field.onChange}
                      fullWidth
                    >
                      <MenuItem value="ADMIN">Admin</MenuItem>
                      <MenuItem value="VENDOR">Vendor</MenuItem>
                      <MenuItem value="STUDENT">Student</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12}  sx={{mt: 1.8}}>
              <Controller
                control={control}
                name="userIds"
                id="userIds"
                rules={{ required: "Select users"}}
                render={({field}) => (
                  <Autocomplete
                    {...field}
                    multiple
                    size="small"
                    id="users"
                    options={adminUsers}
                    disableCloseOnSelect
                    filterOptions={filterOptions} 
                    value={(field.value) || []}
                    onChange={(e, newValue) => field.onChange(newValue)}
                    // getOptionSelected={(option) => option.displayName}
                    // onInputChange={onChange}
                    getOptionLabel={(option) => option.displayName}
                    renderOption={(props, option, { selected }) => {
                      return (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                          // checked={isAllOptionSelected(field.value) && option.userId !== -1 ? false : selected}
                          // disabled={isAllOptionSelected(field.value) && option.userId !== -1 }
                          // readOnly={isAllOptionSelected(field.value) && option.userId !== -1 }
                        />
                        {option.displayName}
                      </li>
                    )}}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        error={!!errors?.userIds}  
                        InputProps={{
                          ...field,
                          ...params.InputProps,
                        }}
                        required 
                        label="Users" 
                        placeholder="Users" 
                      />
                    )}
                  />
                )}
                onChange={([, data]) => data}
              />
            </Grid>

          </Grid>

          <Grid item xs={12} sm={6} md={6} xl={8} lg={8} textAlign='center'>
            <Stack direction="column"  justifyContent="center" alignItems="center">
              <Typography component="h2" variant="body1" gutterBottom sx={{mb: 1}}>
                Notification Image
              </Typography>
              <Avatar variant="square" src={notificationImage} sx={{ border: '1px solid #ddd', width: 280, height: 280, cursor: 'pointer' }} onClick={onNotificationPhotoClick}>
                <NoPhotographyIcon  sx={{ width: 280, height: 280, cursor: 'pointer' }} />
              </Avatar> 
              <Box sx={{visibility: 'hidden', width: 0, height: 0}}>
                <Controller
                  control={control}
                  name="image"
                  id="image"
                  render={({field }) => (
                    <TextField
                    {...field}
                    margin="normal"
                    type="file"
                    fullWidth
                    required
                    onChange={(e) => {uploadPhoto(e); return true;}}
                    inputRef={fileUploadInutRef}
                    />
                  )}
                />
              </Box>
              {notificationImage ? <Button sx={{mt: 2, textTransform: 'none'}} color="error" variant="contained" onClick={() => clearImage()}>Delete Image</Button> : null}
            </Stack>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Button
              type="submit"
              variant="contained"
              sx={{ mt: 3, mb: 2, textTransform: 'inherit' }}
              color="primary"
            >
              Submit
            </Button>
          </Grid>
          
        </Grid>
      </Box> 
    </Paper>
  );
}
 
export default NotificationsPage;
